import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../supabase/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  const loadUserProfile = async (userId) => {
    try {
      if (!userId) return;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error && error.code === 'PGRST116') {
        // Table exists but record doesn't - Create it
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert([{ 
            user_id: userId, 
            display_name: user?.email?.split('@')[0] || 'Trader'
          }])
          .select().single()
        if (!createError) setProfile(newProfile)
      } else if (!error) {
        setProfile(data)
      }
    } catch (err) {
      console.error('Profile fetch failed (Check if table exists):', err)
    }
  }

  useEffect(() => {
    let mounted = true

    // Safety timeout: Never stay stuck in loading more than 4 seconds
    const timeout = setTimeout(() => {
      if (mounted && loading) setLoading(false)
    }, 4000)

    async function init() {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (mounted) {
          if (session?.user) {
            setUser(session.user)
            await loadUserProfile(session.user.id)
          }
          setLoading(false)
        }
      } catch (e) {
        console.error('Auth Init Error:', e)
        if (mounted) setLoading(false)
      }
    }

    init()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setUser(session?.user ?? null)
        if (session?.user) loadUserProfile(session.user.id)
        else setProfile(null)
        setLoading(false)
      }
    })

    return () => {
      mounted = false
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [])

  const value = {
    user,
    profile,
    loading,
    signIn: (email, password) => supabase.auth.signInWithPassword({ email, password }),
    signUp: (email, password) => supabase.auth.signUp({ email, password }),
    signOut: () => supabase.auth.signOut(),
  }

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 font-sans">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-500 animate-pulse">Establishing secure connection...</p>
          </div>
        </div>
      ) : children}
    </AuthContext.Provider>
  )
}